// People: who a handle means, who a task is with, and everything about one
// person gathered for their page. Pure; the links are `references` edges to
// the person (tasks with, for or waiting on them; their dates), and what they
// sent is matched by handle on the `from` label, so nothing stored changes.
import type { Edge } from '@/db/types'
import type { PersonResource } from '@/kinds/person/apply'
import { keyOf } from '@/kinds/person/schema'
import { memberName } from '@/kinds/grant/schema'
import type { TaskResource } from '@/kinds/task/apply'
import { isOpen, isTemplate } from '@/kinds/task/schema'
import type { DateResource } from '@/kinds/date/apply'
import { nextOccurrence } from './dates'
import { daysBetween } from './time'

/** Every handle that means this person: the key and each aka, as handles. */
export const handlesOf = (p: PersonResource): string[] => [
  p.spec.key,
  ...(p.spec.aka ?? []).map(keyOf).filter(Boolean),
]

/** The person a handle or name means ("keira", "Keira", "k"; "me" is the owner), if any. */
export const personFor = (
  people: PersonResource[],
  handle: string | undefined,
): PersonResource | undefined => {
  if (!handle) return undefined
  const h = keyOf(handle)
  if (!h) return undefined
  // @me is the owner
  if (h === 'me') return people.find((p) => !p.deleted_at && p.spec.me)
  return people.find((p) => !p.deleted_at && handlesOf(p).includes(h))
}

/** The ids of the people a thing points at. */
export const linkedPeople = (
  id: string,
  edges: Edge[],
  people: Map<string, PersonResource>,
): PersonResource[] =>
  edges
    .filter((e) => e.from_id === id && e.relation === 'references' && !e.deleted_at && people.has(e.to_id))
    .map((e) => people.get(e.to_id)!)

/** What the app calls someone: "You" for the owner, else their name. */
export const nameOf = (p: PersonResource): string => (p.spec.me ? 'You' : p.spec.name)

/**
 * What a workspace member is called (mirrors member.NameWith): the name the owner set,
 * else the first name of the person her grant's `from` names, else the grant's label.
 */
export const memberNameWith = (
  spec: { name?: string; member_name?: string; from?: string },
  people: PersonResource[],
): string => {
  if (spec.member_name?.trim()) return memberName(spec)
  const p = spec.from ? personFor(people, spec.from) : undefined
  return p && !p.spec.me ? (p.spec.name.split(/\s+/)[0] ?? p.spec.name) : memberName(spec)
}

/** A first name, or "You". */
export const firstOf = (p: PersonResource): string =>
  p.spec.me ? 'You' : (p.spec.name.split(/\s+/)[0] ?? p.spec.name)

/**
 * Who a thing is with: "You and Keira", "Keira, Sam and Robin". The owner
 * comes first as "You". Mirrors person.Together.
 */
export const togetherLine = (people: PersonResource[]): string => {
  const names = [...people.filter((p) => p.spec.me), ...people.filter((p) => !p.spec.me)].map(firstOf)
  if (names.length < 2) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** Initials for an avatar: "Keira Smith" → "KS", "mum" → "M". */
export const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => [...w][0]?.toUpperCase() ?? '')
    .join('')

export interface PersonView {
  /** their dates, soonest first, with the next occurrence */
  dates: { date: DateResource; on: string; days: number }[]
  /** open tasks waiting on them */
  waiting: TaskResource[]
  /** other open tasks with or for them */
  with: TaskResource[]
  /** what they sent: tasks carrying their handle as `from`, newest first */
  from: TaskResource[]
}

/** Everything about one person, from the tasks, dates and edges on this device. */
export const gather = (
  p: PersonResource,
  tasks: TaskResource[],
  dates: DateResource[],
  edges: Edge[],
  today: string,
): PersonView => {
  const linked = new Set(
    edges
      .filter((e) => e.to_id === p.id && e.relation === 'references' && !e.deleted_at)
      .map((e) => e.from_id),
  )
  const open = tasks.filter((t) => !t.deleted_at && !isTemplate(t.spec) && isOpen(t.status.state))
  const mine = open.filter((t) => linked.has(t.id))
  const handles = new Set(handlesOf(p))
  // the owner's own captures are not something they were sent
  const from = p.spec.me
    ? []
    : tasks
        .filter(
          (t) => !t.deleted_at && !isTemplate(t.spec) && t.labels.from && handles.has(keyOf(t.labels.from)),
        )
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
  const theirs = dates
    .filter((d) => !d.deleted_at && linked.has(d.id))
    .map((d) => ({ date: d, on: nextOccurrence(d.spec, today) }))
    .filter((x) => x.on)
    .map((x) => ({ ...x, days: daysBetween(today, x.on) }))
    .sort((a, b) => a.on.localeCompare(b.on))
  return {
    dates: theirs,
    waiting: mine.filter((t) => t.status.state === 'waiting'),
    with: mine.filter((t) => t.status.state !== 'waiting'),
    from,
  }
}
